import { useEffect, useCallback, useState, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import {
  FeatureCollection,
  GeoJsonProperties,
  Polygon,
  MultiPolygon,
} from 'geojson';
import { OutageDetailsByCounty } from '../../../__generated__/pge-types';
import { getPolygonCenter } from '../components/account-marker-overlay/utils';

const COUNTY_ARCGIS_URL =
  'https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/USA_Boundaries_2023/FeatureServer/2/query';

interface FetchParams {
  counties: string[];
}

const fetchCountyPolygons = async ({
  counties,
}: FetchParams): Promise<FeatureCollection<
  Polygon | MultiPolygon,
  GeoJsonProperties
>> => {
  if (!counties.length) {
    return { type: 'FeatureCollection', features: [] };
  }

  const countiesWithSuffix = counties.map(
    c => `${c.charAt(0).toUpperCase() + c.slice(1).toLowerCase()} County`,
  );

  const where = `STATE_ABBR='OR' AND NAME IN (${countiesWithSuffix
    .map(n => `'${n.replace(/'/g, '\'\'')}'`)
    .join(',')})`;

  const params = new URLSearchParams({
    where,
    outFields: 'NAME,STATE_ABBR',
    returnGeometry: 'true',
    outSR: '4326',
    f: 'geojson',
  });

  const url = `${COUNTY_ARCGIS_URL}?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(
      `Failed to fetch counties: ${res.status} ${res.statusText}`,
    );
  }
  const json = (await res.json()) as FeatureCollection<
    Polygon | MultiPolygon,
    GeoJsonProperties
  >;
  return json;
};

export const useGetCountyBoundaries = (
  mapInstance: mapboxgl.Map | null,
  outagesData: OutageDetailsByCounty[],
  shouldRun: boolean = true,
) => {
  const [countyCenters, setCountyCenters] = useState<
    { county: string; count: number; center: [number, number] }[]
  >([]);
  const [geoJson, setGeoJson] = useState<
    FeatureCollection<Polygon | MultiPolygon, GeoJsonProperties>
  >();

  const countyNames = useMemo(() => {
    return [
      ...new Set(
        outagesData
          ?.map(o => o?.county)
          .filter((c): c is string => typeof c === 'string'),
      ),
    ];
  }, [outagesData]);

  const updateLayer = useCallback(
    async (map: mapboxgl.Map) => {
      if (!outagesData?.length || !shouldRun) {
        return;
      }

      const geojson = await fetchCountyPolygons({ counties: countyNames });
      if (!geojson) {
        return;
      }
      setGeoJson(geojson);

      // Compute centroids for markers
      const centers = geojson.features
        .map(f => {
          if (!f?.geometry) {
            return null;
          }
          // const c = turf.centroid(f);
          const coords = getPolygonCenter(f.geometry);
          const countyName = (f.properties?.NAME || '')
            .replace(/ County$/i, '')
            .trim()
            .toUpperCase();
          const outage = outagesData.find(
            o => o?.county?.toUpperCase() === countyName,
          );
          return {
            county: countyName,
            count: outage?.totalCustomersImpacted ?? 0,
            center: coords,
          };
        })
        .filter(Boolean) as {
        county: string;
        count: number;
        center: [number, number];
      }[];

      setCountyCenters(centers);
    },
    [outagesData, shouldRun],
  );

  useEffect(() => {
    if (!mapInstance) {
      return;
    }
    void updateLayer(mapInstance);
  }, [mapInstance, updateLayer]);

  return { countyCenters, geoJson };
};
